<template>
  <div
    class="
      product-carousel
      content-carousel
      critical-component-clear
      section-content-padding
    "
  >
    <div
      :class="{
        'container--reset-padding': products.length <= carouselThreshold
      }"
      class="container grid no-row-gap"
    >
      <div class="col xs4 l3 content-carousel__text-description-container">
        <div class="content-carousel__text-description-wrapper">
          <div class="content-carousel__eyebrow">
            <eyebrow
              v-if="liquid.settings.eyebrow"
              :enable-bullet="liquid.settings.eyebrow_bullet"
              :text="liquid.settings.eyebrow"
            />
          </div>

          <highlighter-title
            v-if="liquid.settings.highlighter_copy"
            :highlighter-color="liquid.settings.highlighter_color"
            :highlighter-copy="liquid.settings.highlighter_copy"
            class="content-carousel__title"
            custom-element="h1"
          />

          <p
            v-if="liquid.settings.description"
            class="body-1 content-carousel__description"
            v-text="$cleanRTE(liquid.settings.description)"
          />

          <div
            v-if="liquid.settings.button_url && liquid.settings.button_text"
            class="content-carousel__cta"
          >
            <btn
              :label="liquid.settings.button_text"
              :modifiers="liquid.settings.button_style"
              :url="liquid.settings.button_url"
            />
          </div>
        </div>
      </div>

      <div
        v-if="products.length > carouselThreshold"
        class="col xs4 l9 content-carousel__carousel"
      >
        <button
          v-if="products.length > carouselThreshold"
          :aria-label="$string('accessibility.carousel.previous')"
          :class="`swiper-button-prev-${liquid.sectionId}`"
          class="swiper-arrow swiper-button-prev dark--filled"
        >
          <span
            class="button__icon button-icon"
            v-html="$icon('enclosed.chevron.left')"
          />
        </button>

        <swiper
          ref="swiper"
          :breakpoints="breakpoints"
          :instance-name="liquid.sectionId"
          :loop="false"
          :modules="modules"
          :navigation="navigation"
          :no-swiping-selector="'button'"
          :slides-per-view="2.15"
          :space-between="8"
          @transition-end="updateTabIndex"
          @init="swiperInstance => swiper = swiperInstance"
        >
          <swiper-slide
            v-for="(product, index) in products"
            :id="`product-carousel-slide--${index}-${liquid.sectionId}`"
            :key="`product-carousel-slide-${index}`"
            class="content-carousel__slide"
            js-swiper="slide"
            @keyup="handleSlideKeypress"
            @keydown="handleSlideKeypress"
          >
            <product-card
              :key="`card-${product.id}-${index}`"
              :product="product"
              :index="index"
              :namespace="`product-carousel-${liquid.sectionId}`"
              :preselected-color="getPreselectedColor(product.handle)"
            />
          </swiper-slide>
        </swiper>

        <button
          v-if="products.length > carouselThreshold"
          :aria-label="$string('accessibility.carousel.next')"
          :class="`swiper-button-next-${liquid.sectionId}`"
          class="swiper-arrow swiper-button-next dark--filled"
        >
          <span
            class="button__icon button-icon"
            v-html="$icon('enclosed.chevron.right')"
          />
        </button>
      </div>

      <div
        v-else
        class="grid col xs4 l9 content-carousel__cards"
      >
        <div
          v-for="(product, index) in products"
          :key="product.id"
          class="col xs2 l3 content-carousel__card"
        >
          <product-card
            :key="`card-${product.id}-${index}`"
            :namespace="`product-carousel-${liquid.sectionId}`"
            :preselected-color="getPreselectedColor(product.handle)"
            :product="product"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * A carousel of product cards with text and CTA on the side.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Array} liquid.blocks - Section's blocks.
 * @param {String} liquid.settings.eyebrow - Eyebrow text.
 * @param {String} liquid.settings.eyebrow_bullet - Eyebrow toggle.
 * @param {String} liquid.settings.highlighter_copy - Rich text title with
 * (optional) highlights.
 * @param {String} liquid.settings.highlighter_color - The highlight color.
 * @param {String} liquid.settings.description - The description below the
 * title.
 * @param {String} liquid.settings.button_url - The CTA link.
 * @param {String} liquid.settings.button_text - The CTA text.
 * @param {String} liquid.settings.button_style - Dark or Secondary.
 * @param {Array} liquid.productHandles - An array of product handles to query.
 */

import { mapState } from 'vuex'

import ProductCard from '~async/product-card/product-card'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper'

import { values } from '~/config/breakpoints'
import { queryProducts } from '~/helpers/query'
import { focusable, updateTabindexOnElement } from '~/helpers/accessibility'

export default {
  name: 'ProductCarousel',
  components: {
    ProductCard,
    Swiper,
    SwiperSlide,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blocks: [],
        sectionId: '',
        settings: {},
        productHandles: [],
      }),
    },
  },

  setup(props) {
    const slides = {
      l: 3,
      xl: 3,
    }
    const breakpoints = {}

    for (const value in values){
      breakpoints[values[value]] = {
        slidesPerView: slides[value] || 2.7,
      }
    }

    breakpoints[values.l] = {
      spaceBetween: 25,
    }

    return {
      modules: [Navigation],
      navigation: {
        nextEl: `.swiper-button-next-${props.liquid.sectionId}`,
        prevEl: `.swiper-button-prev-${props.liquid.sectionId}`,
      },
      breakpoints,
    }
  },

  data() {
    return {
      activeSlideClass: 'swiper-slide-active',
      numVisibleSlides: 3,
      products: [
        false,
        false,
        false,
        false,
      ],
      selectors: {
        slide: '[js-swiper="slide"]',
      },
      swiper: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      currentBreakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Compute the threshold that needs to be met to become a carousel
     * @returns {number}
     */
    carouselThreshold() {
      return this.isMobile ? 2 : 3
    },
  },

  mounted() {
    this.getProducts()
  },

  updated() {
    this.updateTabIndex()
  },

  methods: {

    /**
     * Get the preselected color from block settings based on product handle
     * @param {String} handle - Product handle
     */
    getPreselectedColor(handle) {
      const block = this.liquid.blocks.find(block => {
        return block.settings.product === handle
      })
      if (block) {
        return block.settings.preselected_color
      }
      return ''
    },

    /**
     * Get products from GraphQL
     * @returns {Array}
     */
    async getProducts() {
      const handles = (this.liquid.productHandles || []).filter(Boolean)

      if (!handles.length) {
        throw new Error(
          'No product handles available. Check carousel type, collection, and product cards in theme settings.',
        )
      }

      this.products = await queryProducts(handles)
    },

    /**
     * Move to next slide on mobile when tabbing to the first focusable
     * element.
     * @param event - keydown and keyup events.
     */
    handleSlideKeypress(event) {
      if (event.key !== 'Tab' || !this.isMobile) {
        return
      }
      const slide = event.target.closest(this.selectors.slide)

      if (!slide) {
        return
      }

      const focusableElements = focusable(slide)
      const isActive = slide.classList.contains(this.activeSlideClass)

      if (focusableElements[0] === event.target && !isActive) {
        this.swiper.slideNext()
      }
    },

    /**
     * Change tabindex to -1 for all slides except the active slide and the
     * next 2 slides (3 visible slides total)
     */
    updateTabIndex() {
      if (!this.$refs.swiper) {
        return
      }

      let remainingVisibleCount = 0
      const swiperWrapper = this.$refs.swiper.$el.children[0]

      if (!swiperWrapper?.children) {
        return
      }

      [...swiperWrapper.children].forEach((slide) => {
        const isActive = slide.classList.contains(this.activeSlideClass)
        let tabindex = -1

        if (isActive) {
          remainingVisibleCount = this.numVisibleSlides
        }

        if (remainingVisibleCount > 0) {
          tabindex = 0
          remainingVisibleCount--
        }

        updateTabindexOnElement(slide, tabindex)
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-carousel';
</style>
